// In-app video recorder — the DESKTOP fallback for video evidence (mobile
// uses the native <input capture> camera, which the OS handles better).
// getUserMedia (video + audio) + MediaRecorder with a hard auto-stop at
// MAX_VIDEO_DURATION_SEC,
// then a review screen (Use / Retake). Mirrors useCameraStream's error
// normalization and track-stop discipline, but is its own machine because
// recording needs audio:true and recorder lifecycle that hook doesn't model.
import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import { MAX_VIDEO_DURATION_SEC } from '../../../interface/vesr';
import { CloseSVG } from '../svgs';
import { formatDuration } from './validate';

type RecorderPhase = 'requesting' | 'ready' | 'recording' | 'review' | 'error';

interface IVideoRecorderModalProps {
	onUse: (file: File) => void;
	onClose: () => void;
}

const RECORD_ERRORS: Record<string, string> = {
	'permission-denied': 'Camera or microphone access was denied. Allow both in your browser settings, then try again.',
	'no-camera': 'No camera was found on this device. Pick a video from your files instead.',
	'in-use': 'Your camera is busy in another app. Close it and try again.',
	'unsupported': 'This browser cannot record video. Pick a video from your files instead.',
	'unknown': 'Could not start the camera. Pick a video from your files instead.',
};

const normalizeError = (err: unknown): string => {
	if (typeof window !== 'undefined' && err instanceof DOMException) {
		if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') return 'permission-denied';
		if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') return 'no-camera';
		if (err.name === 'NotReadableError' || err.name === 'TrackStartError') return 'in-use';
	}
	return 'unknown';
};

// Prefer mp4 (plays everywhere incl. iOS) but most desktop browsers only
// record webm — both are on the server's allowlist.
const pickMimeType = (): string => {
	if (typeof MediaRecorder === 'undefined' || !MediaRecorder.isTypeSupported) return '';
	const candidates = ['video/mp4', 'video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
	return candidates.find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
};

const VideoRecorderModal: FC<IVideoRecorderModalProps> = ({ onUse, onClose }) => {
	const [phase, setPhase] = useState<RecorderPhase>('requesting');
	const [errorKey, setErrorKey] = useState('unknown');
	const [elapsed, setElapsed] = useState(0);
	const [reviewUrl, setReviewUrl] = useState<string | null>(null);

	const liveRef = useRef<HTMLVideoElement>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const recorderRef = useRef<MediaRecorder | null>(null);
	const chunksRef = useRef<Blob[]>([]);
	const fileRef = useRef<File | null>(null);
	const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const clearTimers = useCallback(() => {
		if (tickRef.current) {
			clearInterval(tickRef.current);
			tickRef.current = null;
		}
		if (autoStopRef.current) {
			clearTimeout(autoStopRef.current);
			autoStopRef.current = null;
		}
	}, []);

	const stopTracks = useCallback(() => {
		streamRef.current?.getTracks().forEach((t) => t.stop());
		streamRef.current = null;
	}, []);

	const openStream = useCallback(async () => {
		setPhase('requesting');
		if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
			setErrorKey('unsupported');
			setPhase('error');
			return;
		}
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' },
				audio: true,
			});
			streamRef.current = stream;
			if (liveRef.current) liveRef.current.srcObject = stream;
			setPhase('ready');
		} catch (err) {
			setErrorKey(normalizeError(err));
			setPhase('error');
		}
	}, []);

	useEffect(() => {
		void openStream();
		return () => {
			clearTimers();
			if (recorderRef.current && recorderRef.current.state !== 'inactive') {
				try {
					recorderRef.current.stop();
				} catch {
					// Already stopping — nothing to clean.
				}
			}
			recorderRef.current = null;
			streamRef.current?.getTracks().forEach((t) => t.stop());
			streamRef.current = null;
		};
	}, []);

	// Revoke the review object URL whenever it's replaced / on unmount.
	useEffect(
		() => () => {
			if (reviewUrl) URL.revokeObjectURL(reviewUrl);
		},
		[reviewUrl]
	);

	const stopRecording = useCallback(() => {
		clearTimers();
		const recorder = recorderRef.current;
		if (recorder && recorder.state !== 'inactive') recorder.stop();
	}, [clearTimers]);

	const startRecording = useCallback(() => {
		const stream = streamRef.current;
		if (!stream) return;
		const mimeType = pickMimeType();
		let recorder: MediaRecorder;
		try {
			recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
		} catch {
			setErrorKey('unsupported');
			setPhase('error');
			return;
		}
		chunksRef.current = [];
		recorder.ondataavailable = (event) => {
			if (event.data.size) chunksRef.current.push(event.data);
		};
		recorder.onstop = () => {
			const type = (recorder.mimeType || 'video/webm').split(';')[0];
			const ext = type === 'video/mp4' ? 'mp4' : 'webm';
			const blob = new Blob(chunksRef.current, { type });
			const file = new File([blob], `report-video-${Date.now()}.${ext}`, { type });
			fileRef.current = file;
			setReviewUrl(URL.createObjectURL(blob));
			stopTracks();
			setPhase('review');
		};
		recorderRef.current = recorder;
		recorder.start(1000);
		setElapsed(0);
		setPhase('recording');
		tickRef.current = setInterval(() => {
			setElapsed((s) => {
				if (s + 1 >= MAX_VIDEO_DURATION_SEC) stopRecording();
				return s + 1;
			});
		}, 1000);
		// Hard ceiling independent of the UI tick.
		autoStopRef.current = setTimeout(stopRecording, MAX_VIDEO_DURATION_SEC * 1000);
	}, [stopRecording, stopTracks]);

	const retake = useCallback(() => {
		fileRef.current = null;
		setReviewUrl(null);
		setElapsed(0);
		void openStream();
	}, [openStream]);

	const useVideo = useCallback(() => {
		if (fileRef.current) onUse(fileRef.current);
	}, [onUse]);

	const isRecording = phase === 'recording';

	return (
		<div
			className='video-rec'
			role='dialog'
			aria-modal='true'
			aria-label='Record video evidence'
			onKeyDown={(e) => {
				if (e.key === 'Escape') {
					e.preventDefault();
					onClose();
				}
			}}
		>
			<div
				className='video-rec__backdrop'
				onClick={isRecording ? undefined : onClose}
			/>
			<div className='video-rec__panel'>
				<div className='video-rec__bar'>
					<span className='video-rec__title'>
						{phase === 'review' ? 'Review your clip' : isRecording ? 'Recording…' : 'Record video'}
					</span>
					<button
						type='button'
						className='video-rec__close'
						onClick={onClose}
						aria-label='Close recorder'
					>
						<CloseSVG
							width='18'
							height='18'
							color='currentColor'
						/>
					</button>
				</div>

				<div className='video-rec__stage'>
					{phase === 'error' ? (
						<div className='video-rec__message'>
							<p>{RECORD_ERRORS[errorKey]}</p>
							{errorKey === 'permission-denied' ? (
								<button
									type='button'
									className='video-rec__retry'
									onClick={() => void openStream()}
								>
									Try again
								</button>
							) : null}
						</div>
					) : phase === 'review' && reviewUrl ? (
						// eslint-disable-next-line jsx-a11y/media-has-caption -- a just-recorded clip has no caption track
						<video
							className='video-rec__media'
							src={reviewUrl}
							controls
							playsInline
						/>
					) : (
						<>
							<video
								className='video-rec__media'
								ref={liveRef}
								autoPlay
								muted
								playsInline
							/>
							{phase === 'requesting' ? <div className='video-rec__message'>Starting your camera…</div> : null}
							{isRecording ? (
								<span className='video-rec__timer'>
									<span
										className='video-rec__timer-dot'
										aria-hidden='true'
									/>
									{formatDuration(elapsed)} / {formatDuration(MAX_VIDEO_DURATION_SEC)}
								</span>
							) : null}
						</>
					)}
				</div>

				<div className='video-rec__controls'>
					{phase === 'ready' ? (
						<button
							type='button'
							className='video-rec__record'
							onClick={startRecording}
							aria-label='Start recording'
						>
							<span
								className='video-rec__record-dot'
								aria-hidden='true'
							/>
							Record
						</button>
					) : null}
					{isRecording ? (
						<button
							type='button'
							className='video-rec__stop'
							onClick={stopRecording}
							aria-label='Stop recording'
						>
							<span
								className='video-rec__stop-square'
								aria-hidden='true'
							/>
							Stop
						</button>
					) : null}
					{phase === 'review' ? (
						<>
							<button
								type='button'
								className='video-rec__retake'
								onClick={retake}
							>
								Retake
							</button>
							<button
								type='button'
								className='video-rec__use'
								onClick={useVideo}
							>
								Use video
							</button>
						</>
					) : null}
				</div>
			</div>
		</div>
	);
};

export default VideoRecorderModal;
